'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { IoIosClose } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import { setCookiePreferences } from '../../../redux/ducks/StorageDucks';
import type {
  CookiePreference,
  CookieSelectionType,
} from '../../../redux/ducks/StorageDucks.types';
import type { RootReducerType } from '../../../redux/reducers/RootReducer.types';
import Button from '../../button/Button';
import CookieSelection from '../CookieSelection/CookieSelection';

const defaultPreferences: CookieSelectionType = {
  all: true,
  allAnalytics: true,
  allAdvertisement: true,
  allPerformance: true,
  allFunctional: true,
  allOthers: true,
  onesignal: true,
  feedbackfish: true,
};

const noPreferences: CookieSelectionType = {
  all: false,
  allAnalytics: false,
  allAdvertisement: false,
  allPerformance: false,
  allFunctional: false,
  allOthers: false,
  onesignal: false,
  feedbackfish: false,
};

const MIN_SIX_MONTHS: number = 1000 * 60 * 60 * 24 * 31 * 6; // GDPR, six months minimum

const CookieConsent = () => {
  const [isCustomize, setIsCustomize] = useState(false);
  const [onClient, setOnClient] = useState<boolean>(false);
  const acceptAllButtonRef = useRef(null);
  const [preferences, setPreferences] = useState<
    CookieSelectionType | undefined
  >();
  const [cachedPreferences, setCachedPreferences] = useState<
    CookieSelectionType | undefined
  >();
  const dispatch = useDispatch();
  const location = useLocation();

  const cookiePreferences = useSelector(
    (state: RootReducerType) => state.storage.cookiePreferences,
  );
  const { t } = useTranslation();

  const onClose = () => {
    setIsCustomize(false);
  };

  useEffect(() => {
    setOnClient(true);

    return () => {
      setIsCustomize(false);
    };
  }, []);

  const validPreferences = useMemo<boolean>(() => {
    if (cookiePreferences) {
      if (cookiePreferences?.preferences?.all) {
        return true;
      }

      const now = Date.now();
      if (now - cookiePreferences.updated > MIN_SIX_MONTHS) {
        return false;
      }

      return true;
    }

    return false;
  }, [cookiePreferences]);

  useEffect(() => {
    if (preferences === null) {
      return;
    }

    setCachedPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    if (cachedPreferences) {
      // Save preference on redux
      const prefs: CookiePreference = {
        preferences: cachedPreferences,
        updated: Date.now(),
      };
      dispatch(setCookiePreferences(prefs));
      // if logged in update preferences
      // TODO
    }
  }, [cachedPreferences, dispatch]);

  useEffect(() => {
    return () => {
      setIsCustomize(false);
    };
  }, []);

  const noCookieBannerPage = useMemo<boolean>(() => {
    if (!onClient) {
      return false;
    }

    if (validPreferences && !isCustomize) {
      return true;
    }

    if (
      [
        ROUTES.COOKIE_POLICY_ROUTE,
        ROUTES.PRIVACY_POLICY_ROUTE,
        ROUTES.TERMS_AND_CONDITIONS_ROUTE,
      ].indexOf(location.pathname) > -1
    ) {
      return true;
    }

    return false;
  }, [location.pathname, validPreferences, isCustomize, onClient]);

  return (
    <div
      className={`min-w-full flex-col items-center justify-center ${
        noCookieBannerPage === true ? 'hidden' : 'flex'
      }`}
      data-nosnippet
    >
      <div className="flex w-full max-w-7xl items-center justify-center rounded-2xl border-t-2 bg-white drop-shadow-lg">
        <div className="relative flex w-screen min-w-80 flex-col">
          <div className="flex flex-row justify-end min-w-full items-end absolute top-0 right-0">
            <button
              type="button"
              data-test-id="no-cookie-banner-set-no-to-all"
              id="no-cookie-banner-set-no-to-all"
              key="no-cookie-banner-set-no-to-all"
              aria-label={t('static.cookie_banner.banner_btn_reject_label')}
              className="cursor-pointer text-xl font-light text-gray-600 hover:text-gray-300 p-1"
              onClick={() => {
                setCachedPreferences({ ...noPreferences });
                onClose();
              }}
            >
              <IoIosClose size={30} />
            </button>
          </div>
          <div className="p-2 px-4">
            <div className="flex flex-col overscroll-contain gap-4 items-start justify-start">
              <div className="flex shrink-0 flex-row items-center justify-start min-w-full">
                <div className="text-lg md:text-xl font-extrabold">
                  {t('static.cookie_banner.banner_title')}
                </div>
              </div>
              {isCustomize ? (
                <div className="font-thin">
                  <CookieSelection
                    preferences={
                      preferences ||
                      cookiePreferences?.preferences ||
                      defaultPreferences
                    }
                    setPreferences={(p: CookieSelectionType) => {
                      setPreferences(p);
                    }}
                  />
                </div>
              ) : (
                <div className="font-thin text-sm md:text-base">
                  <Trans i18nKey="static.cookie_banner.banner_text">
                    <span className="font-normal">&nbsp;</span>
                    <span className="font-normal">&nbsp;</span>
                    <button
                      type="button"
                      aria-label="Cookie Policy"
                      data-test-id="cookie-policy-btn"
                      id="cookie-policy-btn"
                      key="cookie-policy-btn"
                      className="cursor-pointer text-slate-600 hover:text-sky-600 underline font-bold"
                      onClick={() => {
                        window.open(
                          ROUTES.COOKIE_POLICY_ROUTE,
                          '_blank',
                          'rel=noopener noreferrer',
                        );
                      }}
                    >
                      &nbsp;
                    </button>
                    <button
                      type="button"
                      aria-label="Privacy Policy"
                      data-test-id="privacy-policy-btn"
                      id="privacy-policy-btn"
                      key="privacy-policy-btn"
                      className="cursor-pointer text-slate-600 hover:text-sky-600 underline font-bold"
                      onClick={() => {
                        window.open(
                          ROUTES.PRIVACY_POLICY_ROUTE,
                          '_blank',
                          'rel=noopener noreferrer',
                        );
                      }}
                    >
                      &nbsp;
                    </button>
                  </Trans>
                </div>
              )}

              <div className="flex flex-col justify-evenly md:flex-row min-w-full">
                {!isCustomize ? (
                  <div className="m-1 w-full">
                    <Button
                      label={t(
                        'static.cookie_banner.banner_btn_customize_label',
                      )}
                      disabled={isCustomize}
                      onClick={() => {
                        setIsCustomize(true);
                      }}
                      data-test-id="cookie-customize-button"
                      outline
                      id="cookie-customize-button"
                      small
                    />
                  </div>
                ) : (
                  <div className="m-1 w-full">
                    <Button
                      label={t(
                        'static.cookie_banner.banner_btn_confirm_choices_label',
                      )}
                      disabled={!isCustomize}
                      onClick={() => {
                        if (preferences) {
                          setCachedPreferences({ ...preferences });
                        }
                        setIsCustomize(false);
                        onClose();
                      }}
                      data-test-id="cookie-choices-button"
                      outline
                      id="cookie-choices-button"
                      small
                    />
                  </div>
                )}
                {!isCustomize && (
                  <div className="m-1 w-full">
                    <Button
                      label={t('static.cookie_banner.banner_btn_reject_label')}
                      onClick={() => {
                        setCachedPreferences({ ...noPreferences });
                        onClose();
                      }}
                      data-test-id="cookie-reject-button"
                      outline
                      id="cookie-reject-button"
                      ref={acceptAllButtonRef}
                      small
                    />
                  </div>
                )}
                <div className="m-1 w-full">
                  <Button
                    label={t(
                      'static.cookie_banner.banner_btn_accept_all_label',
                    )}
                    onClick={() => {
                      setPreferences({
                        all: true,
                        allAnalytics: true,
                        allAdvertisement: true,
                        allFunctional: true,
                        allPerformance: true,
                        allOthers: true,
                        onesignal: true,
                        feedbackfish: true,
                      });
                      onClose();
                    }}
                    data-test-id="cookie-accept-button"
                    id="cookie-accept-button"
                    ref={acceptAllButtonRef}
                    small
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
